import React from 'react';
import _ from 'lodash';
import { render, unmountComponentAtNode } from 'react-dom';
import PropTypes from 'prop-types';
import FallbackScreen from 'containers/ErrorBoundary/FallbackScreen';
import muiTheme from '../../theme/muiTheme';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import ERRORS from '../../errors/errorids';
import AppError from '../../errors/apperror';
import Logger from '../../logger/logger';
const log = Logger.get('ErrorBoundary');

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error) {    
		return { hasError: true };  
	}

	static handleUncaughtError = (event, target) => {
		// Ignore error rethrown from ErrorBoundary in DEV environment
		// https://github.com/facebook/react/issues/10474

		if (event &&
			event.message &&
			event.message === 'Uncaught Error: The error you provided does not contain a stack trace.') {
			return;
		}

		const isDevError = event.error.stack && event.error.stack.indexOf('invokeGuardedCallbackDev') !== -1;
		
		if (!isDevError) {
			if (target) {
				const isExtensionError = _.get(event, 'error.stack') && event.error.stack.indexOf('chrome-extension') !== -1;
				this.renderFallBackScreen(target, isExtensionError);
			}
			const options = { visualisers: { feature: 'GLOBAL_UNHANDLED_REJECTION' } };
			this.processError(event, null, options);
		}
	};
	
	static handleUnhandledPromiseRejection = (event, target) => {
		if (event &&
			event.reason &&
			event.reason.message &&
			event.reason.message === 'Action timed out for registerMaster') {
			return;
		}

		if (target) {
			const isExtensionError = _.get(event, 'error.stack') && event.error.stack.indexOf('chrome-extension') !== -1;

			if (event instanceof CustomEvent && event.type === 'unhandledrejection') {
				console.log('possible extension error', event);// eslint-disable-line no-console
			} else {
				this.renderFallBackScreen(target, isExtensionError);
			}
		}
		const options = { visualisers: { feature: 'GLOBAL_UNHANDLED_REJECTION' } };
		this.processError(event, null, options);
	};
  
	componentDidCatch(error, errorInfo) {
		// Called after rendering fallback UI in render()
		// Allows side effects like error-logging
		this.processStateError(error, errorInfo);
	}

	processStateError(error, errorInfo) {
		const options = { visualisers: { feature: 'ERROR_BOUNDARY_ERROR' } };
		this.constructor.processError(error, errorInfo, options);
		this.setState({ hasError: true });
	}

	static processError(event, errorInfo, options) {
		let nErrorProps;
		if (event.reason) {
			const reason = event.reason;
			const message = _.isString(reason) ? reason : _.get(reason, 'message') || 'unknown-unhandled-rejection';

			nErrorProps = _.assign({
				message: message,
				errorId: (event.reason && event.reason.errorId) || ERRORS.UNKNOWN,
				originalError: event.reason,
				errorDetails: {
					...(errorInfo && { errorInfo })
				},
				visualisers: { feature: 'GLOBAL_UNHANDLED_REJECTION' }
			}, options);
		} else {
			nErrorProps = _.assign({
				errorId: ERRORS.UNKNOWN,
				message: event && event.message ? event.message : 'unknown-javascript-error',
				originalError: event.error, // actual error is in ErrorEvent.error
				errorDetails: {
					...(errorInfo && { errorInfo })
				},
				visualisers: { feature: 'GLOBAL_UNCAUGHT_ERROR' },
			}, options);
		}

		log.send(new AppError(nErrorProps));
		this.state && this.setState({ hasError: true });
	}

	static reloadPage = () => {
		window.location = '/?reload-app=true';
	};

	static renderFallBackScreen(target, isExtensionError) {
		if (target) {
			unmountComponentAtNode(target);
			render(
				<MuiThemeProvider theme={muiTheme}>
					<FallbackScreen reload={this.reloadPage} isExtensionError={isExtensionError} />
				</MuiThemeProvider>
				, target
			);
		} else {
			return <FallbackScreen reload={this.reloadPage} />;
		}
	}

	render() {
		const { hasError } = this.state;
		return hasError
			?
			this.constructor.renderFallBackScreen()
			:
			this.props.children
		;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired
};

export default ErrorBoundary;


// WEBPACK FOOTER //
// ./src/containers/ErrorBoundary/ErrorBoundary.js